import { ConditionalRule, ConditionalControl, ConditionalEvalContext, ConditionalSource, FieldConditionState } from './conditional-field.model';

/** Resolve the controlling field's current value from same-sheet data or cross-sheet context. */
function resolveControllingValue(controlledBy: ConditionalSource, data: Record<string, any>, context?: ConditionalEvalContext): any {
  if (typeof controlledBy === 'string') {
    return data?.[controlledBy];
  }
  if (controlledBy.sheet === 'filingInfo') {
    return context?.filingInfo?.[controlledBy.field];
  }
  return undefined;
}

/** A rule is active per `activeWhenValueIn` (value ∈ set) or `inactiveWhenValueIn` (value ∉ set). */
export function isConditionActive(rule: ConditionalRule, data: Record<string, any>, context?: ConditionalEvalContext): boolean {
  const value = resolveControllingValue(rule.controlledBy, data, context);
  if (rule.inactiveWhenValueIn) {
    return !rule.inactiveWhenValueIn.includes(value);
  }
  return (rule.activeWhenValueIn ?? []).includes(value);
}

/** Resolve a field's visibility + validation state under its rule for `data`. */
export function evaluateConditional(rule: ConditionalRule, data: Record<string, any>, context?: ConditionalEvalContext): FieldConditionState {
  const active = isConditionActive(rule, data, context);
  switch (rule.appliesTo) {
    case 'visibility':
      return { visible: active, validationActive: false };
    case 'validation':
      return { visible: true, validationActive: active };
    case 'both':
      return { visible: active, validationActive: active };
  }
}

/**
 * Return a shallow copy of `data` with every field that is currently HIDDEN by
 * a `visibility`/`both` rule removed. Controls without a `conditional` block,
 * and `validation`-only fields, are always kept. Pure — never mutates `data`,
 * and every rule is evaluated against the ORIGINAL `data` + context (order-independent).
 */
export function projectVisibleData(controls: ConditionalControl[], data: Record<string, any>, context?: ConditionalEvalContext): Record<string, any> {
  const projected: Record<string, any> = { ...data };
  for (const control of controls) {
    if (
      control.conditional &&
      control.qname in projected &&
      !evaluateConditional(control.conditional, data, context).visible
    ) {
      delete projected[control.qname];
    }
  }
  return projected;
}
